import {
  BookOpen,
  ChevronRight,
  Clock3,
  Compass,
  Disc3,
  Download,
  DollarSign,
  ExternalLink,
  Heart,
  Headphones,
  Info,
  Loader2,
  Maximize2,
  Moon,
  Music2,
  Newspaper,
  Pause,
  Play,
  Podcast,
  Search,
  Settings,
  SkipBack,
  SkipForward,
  Smartphone,
  Sun,
  Trash2,
  X
} from 'lucide-react';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ATTRIBUTE_OPTIONS, COUNTRIES, DISCOVER_LEGACY_RSS_RAILS, ENTITY_OPTIONS, MEDIA_OPTIONS, NEWS_SOURCES, RSS_PRESETS, countryFlag, countryName, localizedOption } from './lib/constants';
import { comparePrices, formatDate, formatPrice, getArtistInfo, getLegacyRssFeed, getNews, getRssFeed, lookupItunes, searchItunes } from './lib/api';
import { addRecentSearch, addRecentViewed, loadPersistedState, savePersistedState, upsertFavorite } from './lib/storage';
import { createTranslator } from './lib/i18n';
import type { ArtistInfo, FeedItem, Locale, NewsItem, PersistedState, PlayerState, PriceComparisonRow, SearchItem, SearchRecord, ThemeMode, View, ViewedRecord } from './types';

type Route = {
  view: View;
  id?: string;
};

const NAV_ITEMS: Array<{ view: View; icon: typeof Search }> = [
  { view: 'search', icon: Search },
  { view: 'discover', icon: Compass },
  { view: 'compare', icon: DollarSign },
  { view: 'saved', icon: Heart },
  { view: 'news', icon: Newspaper },
  { view: 'settings', icon: Settings }
];

const VALID_VIEWS = new Set<View>(['search', 'discover', 'detail', 'artist', 'compare', 'saved', 'news', 'settings']);

export default function App() {
  const [state, setState] = useState<PersistedState>(() => loadPersistedState());
  const [route, setRoute] = useState<Route>(() => readRoute());
  const [player, setPlayer] = useState<PlayerState>({ queue: [], index: 0, playing: false });
  const [playerExpanded, setPlayerExpanded] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; title: string } | null>(null);
  const [videoLightbox, setVideoLightbox] = useState<{ url: string; title: string; poster?: string; startAt: number } | null>(null);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const t = useMemo(() => createTranslator(state.locale), [state.locale]);

  useEffect(() => {
    const onHashChange = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    savePersistedState(state);
    document.documentElement.lang = state.locale === 'zh' ? 'zh-CN' : 'en';
    if (state.theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.dataset.theme = state.theme;
  }, [state]);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media || !player.item?.previewUrl) return;
    if (player.playing) {
      media.play().catch(() => setPlayer((current) => ({ ...current, playing: false })));
    } else {
      media.pause();
    }
  }, [player.playing, player.item?.kind, player.item?.previewUrl, player.item?.wrapperType, playerExpanded]);

  const navigate = useCallback((view: View, id?: string) => {
    window.location.hash = id ? `#/${view}/${encodeURIComponent(id)}` : `#/${view}`;
  }, []);

  const patchState = useCallback((patch: Partial<PersistedState>) => {
    setState((current) => ({ ...current, ...patch }));
  }, []);

  const toggleFavorite = useCallback((item: SearchItem) => {
    setState((current) => ({ ...current, favorites: upsertFavorite(current.favorites, item) }));
  }, []);

  const isFavorite = useCallback((item: SearchItem) => {
    return state.favorites.some((favorite) => favorite.id === item.id);
  }, [state.favorites]);

  const playQueue = useCallback((queue: SearchItem[], index = 0) => {
    const playableQueue = queue.filter((candidate) => candidate.previewUrl);
    const playableIndex = Math.max(0, Math.min(index, playableQueue.length - 1));
    const item = playableQueue[playableIndex];
    if (!item) return;
    setPlayer({ item, queue: playableQueue, index: playableIndex, playing: true });
  }, []);

  const playItem = useCallback((item: SearchItem, queue?: SearchItem[]) => {
    setPlayer((current) => {
      const nextQueue = queue?.filter((candidate) => candidate.previewUrl) ?? [item];
      const index = Math.max(0, nextQueue.findIndex((candidate) => candidate.id === item.id));
      const sameItem = current.item?.id === item.id;
      return { item: nextQueue[index] ?? item, queue: nextQueue, index, playing: sameItem ? !current.playing : true };
    });
  }, []);

  const playItemWithPreview = useCallback((item: SearchItem, queue?: SearchItem[]) => {
    playItem(item, queue);
    if (isVideoItem(item)) setPlayerExpanded(true);
  }, [playItem]);

  const movePlayer = useCallback((direction: -1 | 1) => {
    setPlayer((current) => {
      if (!current.queue.length) return { ...current, playing: false };
      const nextIndex = current.index + direction;
      if (nextIndex < 0 || nextIndex >= current.queue.length) {
        return { ...current, playing: false };
      }
      return { ...current, index: nextIndex, item: current.queue[nextIndex], playing: true };
    });
  }, []);

  const openDetail = useCallback((item: SearchItem) => {
    setState((current) => ({ ...current, recentViewed: addRecentViewed(current.recentViewed, item) }));
    navigate('detail', item.id);
  }, [navigate]);

  const common = {
    t,
    locale: state.locale,
    state,
    patchState,
    navigate,
    toggleFavorite,
    isFavorite,
    playItem: playItemWithPreview,
    playQueue,
    movePlayer,
    openDetail,
    openArtwork: (url: string, title: string) => setLightbox({ url, title }),
    player
  };

  return (
    <div className="app-shell" style={{ '--card-min': `${state.cardSize}px` } as React.CSSProperties}>
      <Sidebar route={route} navigate={navigate} t={t} favoriteCount={state.favorites.length} />
      <main className="main-area">
        <TopBar
          route={route}
          t={t}
          mediaRef={mediaRef}
          player={player}
          setPlayer={setPlayer}
          movePlayer={movePlayer}
          navigate={navigate}
          expanded={playerExpanded}
          onExpandedChange={setPlayerExpanded}
          onOpenVideo={(item, startAt) => {
            if (!item.previewUrl) return;
            mediaRef.current?.pause();
            setPlayer((current) => ({ ...current, playing: false }));
            setVideoLightbox({ url: item.previewUrl, title: item.title, poster: item.artworkUrl, startAt });
          }}
        />
        <section className="content-area">
          {route.view === 'search' && <SearchPage {...common} />}
          {route.view === 'discover' && <DiscoverPage {...common} />}
          {route.view === 'detail' && <DetailPage {...common} id={route.id} />}
          {route.view === 'artist' && <ArtistPage {...common} id={route.id} />}
          {route.view === 'compare' && <ComparePage {...common} id={route.id} />}
          {route.view === 'saved' && <SavedPage {...common} />}
          {route.view === 'news' && <NewsPage {...common} />}
          {route.view === 'settings' && <SettingsPage {...common} />}
        </section>
        {route.view !== 'settings' && <GlobalFooterControls t={t} locale={state.locale} state={state} patchState={patchState} />}
      </main>
      {lightbox && <ArtworkLightbox lightbox={lightbox} onClose={() => setLightbox(null)} t={t} />}
      {videoLightbox && <VideoLightbox video={videoLightbox} onClose={() => setVideoLightbox(null)} t={t} />}
    </div>
  );
}

function Sidebar({ route, navigate, t, favoriteCount }: { route: Route; navigate: (view: View, id?: string) => void; t: ReturnType<typeof createTranslator>; favoriteCount: number }) {
  return (
    <aside className="sidebar">
      <button className="brand" onClick={() => navigate('search')} aria-label={t('appName')}>
        <span className="brand-mark apple-brand-mark" aria-hidden="true"></span>
        <span>{t('appName')}</span>
      </button>
      <nav className="nav-list" aria-label="Primary">
        {NAV_ITEMS.map(({ view, icon: Icon }) => (
          <button key={view} className={`nav-item ${route.view === view ? 'is-active' : ''}`} onClick={() => navigate(view)} aria-label={t(view)}>
            <Icon size={18} />
            <span>{t(view)}</span>
            {view === 'saved' && favoriteCount > 0 && <span className="nav-badge">{favoriteCount}</span>}
          </button>
        ))}
      </nav>
    </aside>
  );
}

function TopBar({ route, t, mediaRef, player, setPlayer, movePlayer, navigate, expanded, onExpandedChange, onOpenVideo }: { route: Route; t: ReturnType<typeof createTranslator>; mediaRef: React.MutableRefObject<HTMLMediaElement | null>; player: PlayerState; setPlayer: React.Dispatch<React.SetStateAction<PlayerState>>; movePlayer: (direction: -1 | 1) => void; navigate: (view: View, id?: string) => void; expanded: boolean; onExpandedChange: (expanded: boolean) => void; onOpenVideo: (item: SearchItem, startAt: number) => void }) {
  const title = route.view === 'detail' ? t('detail') : t(route.view);
  return (
    <header className="topbar">
      <div>
        <p className="eyebrow">Apple</p>
        <h1>{title}</h1>
      </div>
      <div className="topbar-actions">
        <PlayerBar mediaRef={mediaRef} player={player} setPlayer={setPlayer} movePlayer={movePlayer} navigate={navigate} t={t} expanded={expanded} onExpandedChange={onExpandedChange} onOpenVideo={onOpenVideo} />
      </div>
    </header>
  );
}

function GlobalFooterControls({ t, locale, state, patchState }: { t: ReturnType<typeof createTranslator>; locale: Locale; state: PersistedState; patchState: (patch: Partial<PersistedState>) => void }) {
  const handleCardSizeChange = (event: React.FormEvent<HTMLInputElement>) => {
    patchState({ cardSize: Number(event.currentTarget.value) });
  };

  return (
    <footer className="page-bottom-controls" aria-label={t('globalControls')}>
      <div>
        <p className="eyebrow">Apple</p>
        <h2>{t('globalControls')}</h2>
        <p>{t('globalControlsHint')}</p>
      </div>
      <Field label={t('defaultCountry')}>
        <select value={state.defaultCountry} onChange={(event) => patchState({ defaultCountry: event.target.value })}>
          {COUNTRIES.map((country) => <option key={country.code} value={country.code}>{countryLabel(country.code, locale)}</option>)}
        </select>
      </Field>
      <Field label={`${t('cardSize')} ${state.cardSize}px`}>
        <input
          className="size-slider"
          type="range"
          min="150"
          max="260"
          step="10"
          value={state.cardSize}
          onInput={handleCardSizeChange}
          onChange={handleCardSizeChange}
        />
      </Field>
      <Field label={t('theme')}>
        <Segmented<ThemeMode>
          value={state.theme}
          options={[
            { value: 'system', label: t('system'), icon: Settings },
            { value: 'light', label: t('light'), icon: Sun },
            { value: 'dark', label: t('dark'), icon: Moon }
          ]}
          onChange={(theme) => patchState({ theme })}
        />
      </Field>
    </footer>
  );
}

function SearchPage(props: CommonProps) {
  const { t, locale, state, patchState, playItem, openDetail, toggleFavorite, isFavorite, player } = props;
  const [term, setTerm] = useState('');
  const [media, setMedia] = useState('music');
  const [entity, setEntity] = useState('song');
  const [attribute, setAttribute] = useState('');
  const [country, setCountry] = useState(state.defaultCountry);
  const [limit, setLimit] = useState(50);
  const [explicit, setExplicit] = useState<'Yes' | 'No'>('Yes');
  const [results, setResults] = useState<SearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const entityOptions = ENTITY_OPTIONS[media] ?? ENTITY_OPTIONS.all;

  useEffect(() => {
    const options = ENTITY_OPTIONS[media] ?? ENTITY_OPTIONS.all;
    setEntity(options[0]?.value ?? '');
  }, [media]);

  const runSearch = async (override?: Partial<{ term: string; media: string; entity: string; country: string }>) => {
    const nextTerm = override?.term ?? term;
    const nextMedia = override?.media ?? media;
    const nextEntity = override?.entity ?? entity;
    const nextCountry = override?.country ?? country;
    if (!nextTerm.trim()) return;
    setLoading(true);
    setError('');
    try {
      const items = await searchItunes({
        term: nextTerm.trim(),
        country: nextCountry,
        media: nextMedia,
        entity: nextEntity,
        attribute,
        limit,
        lang: 'en_us',
        explicit
      });
      setResults(items);
      patchState({ recentSearches: addRecentSearch(state.recentSearches, { term: nextTerm.trim(), media: nextMedia, entity: nextEntity, country: nextCountry }) });
    } catch (searchError) {
      setError(searchError instanceof Error ? searchError.message : t('error'));
    } finally {
      setLoading(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void runSearch();
  };

  const replaySearch = (record: SearchRecord) => {
    setTerm(record.term);
    setMedia(record.media);
    setEntity(record.entity ?? '');
    setCountry(record.country);
    void runSearch(record);
  };

  const removeRecentSearch = (record: SearchRecord) => {
    patchState({ recentSearches: state.recentSearches.filter((item) => item.id !== record.id) });
  };

  return (
    <div className="page-stack">
      <form className="search-panel" onSubmit={submit}>
        <div className="search-input-wrap">
          <Search size={22} />
          <input value={term} onChange={(event) => setTerm(event.target.value)} placeholder={t('searchPlaceholder')} autoFocus />
          <button className="primary-button" type="submit" disabled={loading || !term.trim()}>
            {loading ? <Loader2 className="spin" size={17} /> : <Search size={17} />}
            <span>{t('searchNow')}</span>
          </button>
        </div>
        <div className="filter-grid">
          <Field label={t('media')}>
            <select value={media} onChange={(event) => setMedia(event.target.value)}>
              {MEDIA_OPTIONS.map((option) => <option key={option.value} value={option.value}>{localizedOption(option, locale)}</option>)}
            </select>
          </Field>
          <Field label={t('entity')}>
            <select value={entity} onChange={(event) => setEntity(event.target.value)}>
              {entityOptions.map((option) => <option key={option.value} value={option.value}>{localizedOption(option, locale)}</option>)}
            </select>
          </Field>
          <Field label={t('attribute')}>
            <select value={attribute} onChange={(event) => setAttribute(event.target.value)}>
              {ATTRIBUTE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{localizedOption(option, locale)}</option>)}
            </select>
          </Field>
          <Field label={t('country')}>
            <select value={country} onChange={(event) => setCountry(event.target.value)}>
              {COUNTRIES.map((item) => <option key={item.code} value={item.code}>{countryLabel(item.code, locale)}</option>)}
            </select>
          </Field>
          <Field label={t('limit')}>
            <select value={limit} onChange={(event) => setLimit(Number(event.target.value))}>
              {[25, 50, 100, 200].map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </Field>
          <Field label={t('explicit')}>
            <Segmented<'Yes' | 'No'> value={explicit} options={[{ value: 'Yes', label: t('yes') }, { value: 'No', label: t('no') }]} onChange={setExplicit} />
          </Field>
        </div>
      </form>

      {state.recentSearches.length > 0 && (
        <section className="quick-row" aria-label={t('recentSearches')}>
          {state.recentSearches.slice(0, 8).map((record) => (
            <span key={record.id} className="history-chip">
              <button className="chip-main" onClick={() => replaySearch(record)}>
                <Clock3 size={14} />
                <span>{record.term}</span>
              </button>
              <button className="chip-remove" onClick={() => removeRecentSearch(record)} aria-label={`${t('remove')} ${record.term}`}>
                <X size={14} />
              </button>
            </span>
          ))}
        </section>
      )}

      <SectionHeader title={t('results')} meta={results.length ? `${results.length}` : undefined} />
      {loading && <ShimmerGrid />}
      {error && <ErrorState message={error} />}
      {!loading && !error && results.length === 0 && <EmptyState icon={Search} text={t('noResults')} />}
      {!loading && !error && results.length > 0 && (
        <div className="result-grid">
          {results.map((item) => (
            <MediaCard
              key={`${item.id}-${item.kind}-${item.title}`}
              item={item}
              t={t}
              player={player}
              onPlay={playItem}
              onOpen={openDetail}
              onFavorite={toggleFavorite}
              favorite={isFavorite(item)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DiscoverPage({ t, locale, state, patchState, playItem, playQueue, openDetail, toggleFavorite, isFavorite, player }: CommonProps) {
  const [presetId, setPresetId] = useState(RSS_PRESETS.some((item) => item.id === state.discoverPresetId) ? state.discoverPresetId : RSS_PRESETS[0].id);
  const [country, setCountry] = useState(state.discoverCountry || state.defaultCountry);
  const [limit, setLimit] = useState(state.discoverLimit);
  const [feed, setFeed] = useState<{ title: string; updated?: string; items: FeedItem[] }>({ title: '', items: [] });
  const [legacyRails, setLegacyRails] = useState<Record<string, SearchItem[]>>({});
  const [legacyRailsLoading, setLegacyRailsLoading] = useState(false);
  const [legacyRailsError, setLegacyRailsError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const preset = RSS_PRESETS.find((item) => item.id === presetId) ?? RSS_PRESETS[0];
  const feedOptions = RSS_PRESETS.map((item) => ({ value: item.id, label: localizedOption(item, locale), icon: rssPresetIcon(item.id) }));
  const PresetIcon = rssPresetIcon(preset.id);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getRssFeed({
      storefront: country.toLowerCase(),
      media: preset.media,
      feed: preset.feed,
      limit,
      type: preset.type
    }).then((data) => {
      if (!cancelled) setFeed({ title: data.title, updated: data.updated, items: data.items });
    }).catch((rssError) => {
      if (!cancelled) setError(rssError instanceof Error ? rssError.message : t('error'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [country, limit, preset.feed, preset.media, preset.type, t]);

  useEffect(() => {
    let cancelled = false;
    setLegacyRailsLoading(true);
    setLegacyRailsError('');
    Promise.all(DISCOVER_LEGACY_RSS_RAILS.map(async (rail) => {
      const data = await getLegacyRssFeed({
        storefront: country.toLowerCase(),
        feed: rail.feed,
        limit
      });
      return [rail.id, data.items] as const;
    })).then((entries) => {
      if (!cancelled) setLegacyRails(Object.fromEntries(entries));
    }).catch((legacyError) => {
      if (!cancelled) setLegacyRailsError(legacyError instanceof Error ? legacyError.message : t('error'));
    }).finally(() => {
      if (!cancelled) setLegacyRailsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [country, limit, t]);

  const searchFromFeed = (item: FeedItem): SearchItem => ({
    id: item.id,
    rawId: item.id,
    kind: item.kind ?? preset.type,
    title: item.name,
    subtitle: [item.artistName, item.kind, item.genres[0]].filter(Boolean).join(' • '),
    artistName: item.artistName,
    artworkUrl: item.artworkUrl,
    officialUrl: item.url,
    releaseDate: item.releaseDate,
    primaryGenreName: item.genres[0],
    raw: item.raw
  });

  const playFeedItem = async (feedItem: FeedItem) => {
    const item = searchFromFeed(feedItem);
    if (item.previewUrl) {
      playItem(item);
      return;
    }

    try {
      const lookupEntity = feedItem.kind === 'albums' || preset.type === 'albums' ? 'song' : undefined;
      const lookupItems = await lookupItunes({ id: item.id, country, entity: lookupEntity, limit: 50 });
      const queue = lookupItems.filter((candidate) => candidate.previewUrl);
      if (queue.length > 0) {
        playQueue(queue, 0);
      } else {
        openDetail(item);
      }
    } catch {
      openDetail(item);
    }
  };

  const playLegacyItem = async (item: SearchItem) => {
    if (item.previewUrl) {
      playItem(item);
      return;
    }

    try {
      const lookupItems = await lookupItunes({ id: item.id, country, limit: 10 });
      const previewItem = lookupItems.find((candidate) => candidate.previewUrl);
      if (previewItem) {
        playItem(previewItem);
      } else {
        openDetail(item);
      }
    } catch {
      openDetail(item);
    }
  };

  return (
    <div className="page-stack">
      <div className="toolbar-band">
        <div className="field field-wide">
          <span>{t('feed')}</span>
          <div className="feed-picker-desktop">
            <Segmented
              value={presetId}
              options={feedOptions}
              onChange={(nextPresetId) => {
                setPresetId(nextPresetId);
                patchState({ discoverPresetId: nextPresetId });
              }}
              wrap
            />
          </div>
          <div className="feed-picker-mobile">
            <span className="select-with-icon">
              <PresetIcon size={16} />
              <select value={presetId} onChange={(event) => {
                setPresetId(event.target.value);
                patchState({ discoverPresetId: event.target.value });
              }}>
                {RSS_PRESETS.map((item) => <option key={item.id} value={item.id}>{localizedOption(item, locale)}</option>)}
              </select>
            </span>
          </div>
        </div>
        <Field label={t('country')}>
          <select value={country} onChange={(event) => {
            setCountry(event.target.value);
            patchState({ discoverCountry: event.target.value });
          }}>
            {COUNTRIES.map((item) => <option key={item.code} value={item.code}>{countryLabel(item.code, locale)}</option>)}
          </select>
        </Field>
        <Field label={t('limit')}>
          <select value={limit} onChange={(event) => {
            const nextLimit = Number(event.target.value);
            setLimit(nextLimit);
            patchState({ discoverLimit: nextLimit });
          }}>
            {[10, 25, 50].map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </Field>
      </div>
      <SectionHeader title={feed.title || localizedOption(preset, locale)} meta={feed.updated ? `${t('updated')} ${formatDate(feed.updated)}` : undefined} />
      {loading && <ShimmerGrid />}
      {error && <ErrorState message={error} />}
      {!loading && !error && (
        <div className="result-grid">
          {feed.items.map((feedItem) => {
            const item = searchFromFeed(feedItem);
            return (
              <MediaCard
                key={feedItem.id}
                item={item}
                t={t}
                player={player}
                onPlay={() => playFeedItem(feedItem)}
                onOpen={openDetail}
                onFavorite={toggleFavorite}
                favorite={isFavorite(item)}
                showPlay
              />
            );
          })}
        </div>
      )}
      <section className="discover-search-rails">
        <SectionHeader title={t('movieAndTv')} meta="iTunes RSS" />
        {legacyRailsLoading && <ShimmerGrid />}
        {legacyRailsError && <ErrorState message={legacyRailsError} />}
        {!legacyRailsLoading && !legacyRailsError && DISCOVER_LEGACY_RSS_RAILS.map((rail) => {
          const items = legacyRails[rail.id] ?? [];
          return (
            <div className="page-stack" key={rail.id}>
              <SectionHeader title={localizedOption(rail, locale)} meta={`${items.length}`} />
              {items.length === 0 ? (
                <EmptyState icon={Search} text={t('noMatches')} />
              ) : (
                <div className="result-grid">
                  {items.map((item) => (
                    <MediaCard
                      key={`${rail.id}-${item.id}-${item.title}`}
                      item={item}
                      t={t}
                      player={player}
                      onPlay={playLegacyItem}
                      onOpen={openDetail}
                      onFavorite={toggleFavorite}
                      favorite={isFavorite(item)}
                      showPlay
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}

function DetailPage(props: CommonProps & { id?: string }) {
  const { id, t, state, playItem, toggleFavorite, isFavorite, navigate, player } = props;
  const [item, setItem] = useState<SearchItem | null>(null);
  const [related, setRelated] = useState<SearchItem[]>([]);
  const [artistInfo, setArtistInfo] = useState<ArtistInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setItem(null);
    setRelated([]);
    setArtistInfo(null);
    lookupItunes({ id, country: state.defaultCountry, limit: 50 })
      .then(async (items) => {
        if (cancelled) return;
        const first = items[0] ?? null;
        setItem(first);
        setRelated(items.slice(1));
        if (first?.artistName) {
          getArtistInfo(first.artistName, props.locale).then((info) => {
            if (!cancelled) setArtistInfo(info);
          }).catch(() => undefined);
        }
        if (first) {
          const relatedLookup = relatedLookupFor(first);
          if (relatedLookup) {
            const relatedItems = await lookupItunes({ ...relatedLookup, country: state.defaultCountry, limit: 24 });
            if (!cancelled) setRelated(relatedItems.filter((candidate) => candidate.id !== first.id));
          }
        }
      })
      .catch((detailError) => {
        if (!cancelled) setError(detailError instanceof Error ? detailError.message : t('error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, props.locale, state.defaultCountry, t]);

  useEffect(() => {
    if (!item) return;
    props.patchState({ recentViewed: addRecentViewed(state.recentViewed, item) });
  }, [item]);

  if (!id) return <EmptyState icon={Info} text={t('noMatches')} />;
  if (loading) return <DetailSkeleton />;
  if (error) return <ErrorState message={error} />;
  if (!item) return <EmptyState icon={Info} text={t('noMatches')} />;

  return (
    <div className="page-stack">
      <section className="detail-hero">
        <button className="artwork-button" onClick={() => item.artworkUrl && props.openArtwork(item.artworkUrl, item.title)} aria-label={item.title}>
          <Artwork item={item} size="large" />
        </button>
        <div className="detail-copy">
          <p className="eyebrow">{[item.wrapperType, item.kind, item.primaryGenreName].filter(Boolean).join(' / ')}</p>
          <h2>{item.title}</h2>
          {(item.artistName || item.subtitle) && (
            <p className="detail-subtitle">
              {item.artistName && (
                <button className="inline-artist-button" onClick={() => navigate('artist', String(item.artistId ?? item.artistName))}>
                  {item.artistName}
                </button>
              )}
              {item.collectionName && <span> • {item.collectionName}</span>}
              {!item.artistName && item.subtitle}
            </p>
          )}
          <div className="detail-meta">
            {item.releaseDate && <span>{t('released')} {formatDate(item.releaseDate)}</span>}
            {item.price !== undefined && <span>{t('price')} {formatPrice(item.price, item.currency)}</span>}
            {item.trackCount !== undefined && item.trackCount !== null && <span>{t('trackCount')} {item.trackCount}</span>}
            {item.contentAdvisoryRating && <span>{item.contentAdvisoryRating}</span>}
          </div>
          <div className="action-row">
            {item.previewUrl && <button className="primary-button" onClick={() => playItem(item)}>{player.item?.id === item.id && player.playing ? <Pause size={17} /> : <Play size={17} />}<span>{player.item?.id === item.id && player.playing ? pauseActionLabel(item, t) : previewActionLabel(item, t)}</span></button>}
            <button className="icon-text-button" onClick={() => toggleFavorite(item)}>{isFavorite(item) ? <Heart size={17} fill="currentColor" /> : <Heart size={17} />}<span>{isFavorite(item) ? t('unfavorite') : t('favorite')}</span></button>
            <button className="icon-text-button" onClick={() => navigate('compare', item.id)}><DollarSign size={17} /><span>{t('priceCompare')}</span></button>
            {item.artworkUrl && <a className="icon-text-button" href={artworkDownloadUrl(item)}><Download size={17} /><span>{t('downloadArtwork')}</span></a>}
            {item.officialUrl && <a className="icon-text-button" href={item.officialUrl} target="_blank" rel="noreferrer"><ExternalLink size={17} /><span>{t('openApple')}</span></a>}
          </div>
        </div>
      </section>
      {item.description && (
        <section className="text-section">
          <SectionHeader title={t('about')} />
          <p>{item.description}</p>
        </section>
      )}
      <section className="detail-grid">
        <div className="info-panel">
          <SectionHeader title={t('mediaInfo')} />
          <InfoGrid rows={mediaInfoRows(item, t)} />
        </div>
        <div className="info-panel">
          <SectionHeader title={t('artistInfo')} action={item.artistName ? <button className="ghost-button" onClick={() => navigate('artist', String(item.artistId ?? item.artistName))}>{t('viewArtist')} <ChevronRight size={15} /></button> : undefined} />
          {artistInfo ? <ArtistInfoBlock info={artistInfo} t={t} /> : <p className="muted-text">{item.artistName || item.primaryGenreName || item.kind}</p>}
        </div>
      </section>
      {related.length > 0 && (
        <section className="page-stack">
          <SectionHeader title={t('related')} meta={`${related.length}`} />
          <div className="compact-list">
            {related.map((relatedItem) => (
              <CompactItem key={`${relatedItem.id}-${relatedItem.title}`} item={relatedItem} t={t} onOpen={props.openDetail} onPlay={playItem} player={player} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ArtistPage(props: CommonProps & { id?: string }) {
  const { id, t, locale, state, playItem, playQueue, openDetail, toggleFavorite, isFavorite, player } = props;
  const [artistName, setArtistName] = useState(id ?? '');
  const [artistUrl, setArtistUrl] = useState<string | undefined>();
  const [info, setInfo] = useState<ArtistInfo | null>(null);
  const [albums, setAlbums] = useState<SearchItem[]>([]);
  const [tracks, setTracks] = useState<SearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setAlbums([]);
    setTracks([]);
    setInfo(null);

    const load = async () => {
      let resolvedName = decodeURIComponent(id);
      let resolvedArtistUrl: string | undefined;
      let resolvedAlbums: SearchItem[] = [];

      if (/^\d+$/.test(id)) {
        const artistItems = await lookupItunes({ id, country: state.defaultCountry, entity: 'album', limit: 80 });
        const artist = artistItems.find((item) => item.wrapperType === 'artist') ?? artistItems[0];
        resolvedName = artist?.artistName || artist?.title || resolvedName;
        resolvedArtistUrl = artist?.officialUrl;
        resolvedAlbums = artistItems.filter((item) => item.wrapperType !== 'artist' && item.collectionId);
      }

      const [artistInfo, topTracks] = await Promise.all([
        getArtistInfo(resolvedName, locale).catch(() => null),
        searchItunes({
          term: resolvedName,
          country: state.defaultCountry,
          media: 'music',
          entity: 'song',
          limit: 30,
          lang: 'en_us',
          explicit: 'Yes'
        }).catch(() => [])
      ]);

      if (cancelled) return;
      setArtistName(resolvedName);
      setArtistUrl(resolvedArtistUrl);
      setAlbums(resolvedAlbums);
      setTracks(topTracks);
      setInfo(artistInfo);
    };

    load().catch((artistError) => {
      if (!cancelled) setError(artistError instanceof Error ? artistError.message : t('error'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [id, locale, state.defaultCountry, t]);

  if (!id) return <EmptyState icon={Info} text={t('noMatches')} />;
  if (loading) return <DetailSkeleton />;
  if (error) return <ErrorState message={error} />;

  const heroImage = info?.wikipedia?.imageUrl ?? albums[0]?.artworkUrl ?? tracks[0]?.artworkUrl;
  const playAlbum = async (album: SearchItem) => {
    if (album.previewUrl) {
      playItem(album);
      return;
    }
    const lookupId = album.collectionId ?? album.id;
    const albumTracks = await lookupItunes({ id: lookupId, country: state.defaultCountry, entity: 'song', limit: 80 }).catch(() => []);
    const queue = albumTracks.filter((track) => track.previewUrl);
    if (queue.length > 0) playQueue(queue, 0);
  };

  return (
    <div className="page-stack">
      <section className="artist-hero">
        {heroImage && <img src={heroImage} alt="" />}
        <div>
          <p className="eyebrow">{t('artist')}</p>
          <h2>{artistName}</h2>
          {info?.wikipedia?.summary && <p>{info.wikipedia.summary}</p>}
          <div className="action-row">
            {artistUrl && <a className="icon-text-button" href={artistUrl} target="_blank" rel="noreferrer"><ExternalLink size={17} />{t('openApple')}</a>}
            {info?.wikipedia?.pageUrl && <a className="icon-text-button" href={info.wikipedia.pageUrl} target="_blank" rel="noreferrer"><ExternalLink size={17} />{t('wikipedia')}</a>}
            {info?.musicBrainz?.url && <a className="icon-text-button" href={info.musicBrainz.url} target="_blank" rel="noreferrer"><ExternalLink size={17} />{t('musicBrainz')}</a>}
          </div>
        </div>
      </section>

      {info && (
        <section className="info-panel">
          <SectionHeader title={t('externalData')} />
          <ArtistInfoBlock info={info} t={t} />
        </section>
      )}

      {albums.length > 0 && (
        <section className="page-stack">
          <SectionHeader title={t('collection')} meta={`${albums.length}`} />
          <div className="result-grid">
            {albums.map((album) => (
              <MediaCard key={`${album.id}-${album.title}`} item={album} t={t} player={player} onPlay={playAlbum} onOpen={openDetail} onFavorite={toggleFavorite} favorite={isFavorite(album)} showPlay />
            ))}
          </div>
        </section>
      )}

      {tracks.length > 0 && (
        <section className="page-stack">
          <SectionHeader title={t('results')} meta={`${tracks.length}`} />
          <div className="compact-list">
            {tracks.map((track) => (
              <CompactItem key={`${track.id}-${track.title}`} item={track} t={t} onOpen={openDetail} onPlay={playItem} player={player} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ComparePage({ id, t, state }: CommonProps & { id?: string }) {
  const fallbackId = id ?? state.recentViewed[0]?.id ?? state.favorites[0]?.id ?? '';
  const [itemId, setItemId] = useState(fallbackId);
  const [countries, setCountries] = useState(state.comparisonCountries);
  const [rows, setRows] = useState<PriceComparisonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) setItemId(id);
  }, [id]);

  const runCompare = async () => {
    if (!itemId.trim()) return;
    setLoading(true);
    setError('');
    try {
      setRows(await comparePrices(itemId.trim(), countries));
    } catch (compareError) {
      setError(compareError instanceof Error ? compareError.message : t('error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fallbackId) void runCompare();
  }, []);

  const toggleCountry = (code: string) => {
    setCountries((current) => current.includes(code) ? current.filter((item) => item !== code) : [...current, code].slice(0, 8));
  };

  return (
    <div className="page-stack">
      <div className="compare-panel">
        <Field label={t('itemId')}>
          <input value={itemId} onChange={(event) => setItemId(event.target.value)} placeholder="1440857781" />
        </Field>
        <div className="country-picker">
          {COUNTRIES.map((country) => (
            <label key={country.code} className={`check-chip ${countries.includes(country.code) ? 'is-active' : ''}`}>
              <input type="checkbox" checked={countries.includes(country.code)} onChange={() => toggleCountry(country.code)} />
              <span>{country.code}</span>
            </label>
          ))}
        </div>
        <button className="primary-button" onClick={runCompare} disabled={loading || !itemId.trim()}>
          {loading ? <Loader2 className="spin" size={17} /> : <DollarSign size={17} />}
          <span>{t('priceCompare')}</span>
        </button>
      </div>
      {error && <ErrorState message={error} />}
      {loading && <ShimmerRows />}
      {!loading && rows.length > 0 && (
        <div className="comparison-table">
          {rows.map((row) => (
            <div className="comparison-row" key={row.country}>
              <strong>{row.country}</strong>
              <span>{row.status === 'available' ? row.name : t('unavailable')}</span>
              <span>{row.status === 'available' ? formatPrice(row.price, row.currency) : row.message}</span>
              {row.url && <a href={row.url} target="_blank" rel="noreferrer" aria-label={t('openApple')}><ExternalLink size={17} /></a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SavedPage(props: CommonProps) {
  const { t, state, patchState, openDetail, playItem, player, toggleFavorite } = props;
  return (
    <div className="saved-layout">
      <section className="page-stack">
        <SectionHeader
          title={t('favorites')}
          meta={`${state.favorites.length}`}
          action={state.favorites.length > 0 ? <button className="ghost-button" onClick={() => patchState({ favorites: [] })}><Trash2 size={16} />{t('clear')}</button> : undefined}
        />
        {state.favorites.length === 0 && <EmptyState icon={Heart} text={t('emptySaved')} />}
        <div className="compact-list">
          {state.favorites.map((record) => (
            <CompactItem key={record.id} item={record.item} t={t} onOpen={openDetail} onPlay={playItem} player={player} trailing={<button className="icon-button" onClick={() => toggleFavorite(record.item)} aria-label={t('unfavorite')}><Heart size={17} fill="currentColor" /></button>} />
          ))}
        </div>
      </section>
      <section className="page-stack">
        <SectionHeader title={t('recentViewed')} meta={`${state.recentViewed.length}`} action={state.recentViewed.length > 0 ? <button className="ghost-button" onClick={() => patchState({ recentViewed: [] })}><Trash2 size={16} />{t('clear')}</button> : undefined} />
        <div className="compact-list">
          {state.recentViewed.map((record: ViewedRecord) => (
            <CompactItem key={record.id} item={record.item} t={t} onOpen={openDetail} onPlay={playItem} player={player} />
          ))}
        </div>
        <SectionHeader title={t('recentSearches')} meta={`${state.recentSearches.length}`} action={state.recentSearches.length > 0 ? <button className="ghost-button" onClick={() => patchState({ recentSearches: [] })}><Trash2 size={16} />{t('clear')}</button> : undefined} />
        <div className="history-list">
          {state.recentSearches.map((record) => (
            <div className="history-row" key={record.id}>
              <Clock3 size={16} />
              <span>{record.term}</span>
              <small>{record.media} · {record.country}</small>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function NewsPage({ t, locale }: CommonProps) {
  const [source, setSource] = useState('newsroom');
  const [items, setItems] = useState<NewsItem[]>([]);
  const [title, setTitle] = useState('');
  const [updated, setUpdated] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getNews(source)
      .then((data) => {
        if (!cancelled) {
          setTitle(data.title ?? '');
          setUpdated(data.updated ?? '');
          setItems(data.items);
        }
      })
      .catch((newsError) => {
        if (!cancelled) setError(newsError instanceof Error ? newsError.message : t('error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source, t]);

  return (
    <div className="page-stack">
      <div className="toolbar-band toolbar-single">
        <div className="field field-wide">
          <span>{t('source')}</span>
          <Segmented
            value={source}
            options={NEWS_SOURCES.map((item) => ({ value: item.value, label: localizedOption(item, locale) }))}
            onChange={setSource}
            wrap
          />
        </div>
      </div>
      <SectionHeader title={title || t('news')} meta={updated ? `${t('updated')} ${formatDate(updated)}` : undefined} />
      {loading && <ShimmerRows />}
      {error && <ErrorState message={error} />}
      {!loading && !error && (
        <div className="news-list">
          {items.map((item) => (
            <article className={`news-item ${item.imageUrl ? 'has-image' : 'no-image'}`} key={item.id}>
              {item.imageUrl && <img src={item.imageUrl} alt="" loading="lazy" />}
              <div>
                <p className="eyebrow">{item.publishedAt ? formatDate(item.publishedAt) : item.categories.slice(0, 2).join(' / ')}</p>
                <h3>{item.title}</h3>
                {item.summary && <p>{item.summary}</p>}
                {item.link && <a className="inline-link" href={item.link} target="_blank" rel="noreferrer">{t('officialPage')} <ExternalLink size={14} /></a>}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsPage({ t, locale, state, patchState }: CommonProps) {
  const toggleComparisonCountry = (code: string) => {
    const next = state.comparisonCountries.includes(code)
      ? state.comparisonCountries.filter((item) => item !== code)
      : [...state.comparisonCountries, code].slice(0, 8);
    patchState({ comparisonCountries: next });
  };

  return (
    <div className="settings-grid">
      <section className="settings-section">
        <SectionHeader title={t('theme')} />
        <Segmented<ThemeMode>
          value={state.theme}
          options={[
            { value: 'system', label: t('system'), icon: Settings },
            { value: 'light', label: t('light'), icon: Sun },
            { value: 'dark', label: t('dark'), icon: Moon }
          ]}
          onChange={(theme) => patchState({ theme })}
        />
      </section>
      <section className="settings-section">
        <SectionHeader title={t('language')} />
        <Segmented<Locale>
          value={state.locale}
          options={[
            { value: 'zh', label: t('chinese') },
            { value: 'en', label: t('english') }
          ]}
          onChange={(nextLocale) => patchState({ locale: nextLocale })}
        />
      </section>
      <section className="settings-section">
        <SectionHeader title={t('defaultCountry')} />
        <select value={state.defaultCountry} onChange={(event) => patchState({ defaultCountry: event.target.value })}>
          {COUNTRIES.map((country) => <option key={country.code} value={country.code}>{countryLabel(country.code, locale)}</option>)}
        </select>
      </section>
      <section className="settings-section wide">
        <SectionHeader title={t('compareCountries')} />
        <div className="country-picker">
          {COUNTRIES.map((country) => (
            <label key={country.code} className={`check-chip ${state.comparisonCountries.includes(country.code) ? 'is-active' : ''}`}>
              <input type="checkbox" checked={state.comparisonCountries.includes(country.code)} onChange={() => toggleComparisonCountry(country.code)} />
              <span>{countryLabel(country.code, locale)}</span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}

function MediaCard({ item, t, player, onPlay, onOpen, onFavorite, favorite, showPlay }: { item: SearchItem; t: ReturnType<typeof createTranslator>; player: PlayerState; onPlay: (item: SearchItem) => void | Promise<void>; onOpen: (item: SearchItem) => void; onFavorite: (item: SearchItem) => void; favorite: boolean; showPlay?: boolean }) {
  const isPlaying = player.item?.id === item.id && player.playing;
  const priceText = item.price === undefined || item.price === null ? '' : formatPrice(item.price, item.currency);
  const canPreview = hasUsefulPreviewAction(item, showPlay);
  return (
    <article className="media-card">
      <button className="media-main" onClick={() => onOpen(item)}>
        <Artwork item={item} />
        <span className="media-copy">
          <strong>{item.title}</strong>
          <span>{item.subtitle || item.primaryGenreName || item.kind}</span>
        </span>
      </button>
      <div className="media-meta">
        {priceText && <span>{priceText}</span>}
        <span>{item.releaseDate ? formatDate(item.releaseDate) : item.country}</span>
      </div>
      <div className="card-actions">
        {canPreview && <button className="icon-button" onClick={() => void onPlay(item)} aria-label={isPlaying ? pauseActionLabel(item, t) : previewActionLabel(item, t)}>{isPlaying ? <Pause size={17} /> : <Play size={17} />}</button>}
        <button className="icon-button" onClick={() => onFavorite(item)} aria-label={favorite ? t('unfavorite') : t('favorite')}>{favorite ? <Heart size={17} fill="currentColor" /> : <Heart size={17} />}</button>
        {item.officialUrl && <a className="icon-button" href={item.officialUrl} target="_blank" rel="noreferrer" aria-label={t('openApple')}><ExternalLink size={17} /></a>}
        <button className="icon-button" onClick={() => onOpen(item)} aria-label={t('openDetail')}><ChevronRight size={17} /></button>
      </div>
    </article>
  );
}

function CompactItem({ item, t, onOpen, onPlay, player, trailing }: { item: SearchItem; t: ReturnType<typeof createTranslator>; onOpen: (item: SearchItem) => void; onPlay: (item: SearchItem) => void | Promise<void>; player: PlayerState; trailing?: React.ReactNode }) {
  const isPlaying = player.item?.id === item.id && player.playing;
  const canPreview = hasUsefulPreviewAction(item);
  return (
    <div className="compact-item">
      <button className="compact-main" onClick={() => onOpen(item)}>
        <Artwork item={item} size="small" />
        <span>
          <strong>{item.title}</strong>
          <small>{item.subtitle || item.primaryGenreName || item.kind}</small>
        </span>
      </button>
      <div className="compact-actions">
        {canPreview && <button className="icon-button" onClick={() => onPlay(item)} aria-label={isPlaying ? pauseActionLabel(item, t) : previewActionLabel(item, t)}>{isPlaying ? <Pause size={16} /> : <Play size={16} />}</button>}
        {trailing}
      </div>
    </div>
  );
}

function PlayerBar({ mediaRef, player, setPlayer, movePlayer, navigate, t, expanded, onExpandedChange, onOpenVideo }: { mediaRef: React.MutableRefObject<HTMLMediaElement | null>; player: PlayerState; setPlayer: React.Dispatch<React.SetStateAction<PlayerState>>; movePlayer: (direction: -1 | 1) => void; navigate: (view: View, id?: string) => void; t: ReturnType<typeof createTranslator>; expanded: boolean; onExpandedChange: (expanded: boolean) => void; onOpenVideo: (item: SearchItem, startAt: number) => void }) {
  const [queueOpen, setQueueOpen] = useState(true);
  const [progress, setProgress] = useState({ currentTime: 0, duration: 0 });

  const attachMedia = useCallback((node: HTMLMediaElement | null) => {
    mediaRef.current = node;
  }, [mediaRef]);

  useEffect(() => {
    if (!player.item && expanded) onExpandedChange(false);
  }, [expanded, onExpandedChange, player.item]);

  useEffect(() => {
    setProgress({ currentTime: 0, duration: 0 });
  }, [player.item?.previewUrl]);

  const updateProgress = (event: React.SyntheticEvent<HTMLMediaElement>) => {
    const media = event.currentTarget;
    setProgress({
      currentTime: safeMediaTime(media.currentTime),
      duration: safeMediaTime(media.duration)
    });
  };

  const handleLoadedMetadata = (event: React.SyntheticEvent<HTMLMediaElement>) => {
    const media = event.currentTarget;
    if (progress.currentTime > 0 && Number.isFinite(media.duration) && progress.currentTime < media.duration - 0.5) {
      media.currentTime = progress.currentTime;
    }
    updateProgress(event);
  };

  const seekPreview = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextTime = Number(event.target.value);
    if (!Number.isFinite(nextTime)) return;
    const media = mediaRef.current;
    if (media) media.currentTime = nextTime;
    setProgress((current) => ({ ...current, currentTime: nextTime }));
  };

  if (!player.item) return <audio ref={attachMedia} className="media-hidden" onEnded={() => movePlayer(1)} />;

  const item = player.item;
  const canGoPrevious = player.index > 0;
  const canGoNext = player.index < player.queue.length - 1;
  const showVideoPreview = expanded && isVideoItem(item) && Boolean(item.previewUrl);
  const playPauseLabel = player.playing ? pauseActionLabel(item, t) : previewActionLabel(item, t);
  const selectQueueItem = (index: number) => {
    setPlayer((current) => {
      const nextItem = current.queue[index];
      if (!nextItem) return current;
      return { ...current, index, item: nextItem, playing: true };
    });
  };

  return (
    <>
      <div className="player-bar">
        {!showVideoPreview && (
          <audio
            ref={attachMedia}
            className="media-hidden"
            src={item.previewUrl}
            onEnded={() => movePlayer(1)}
            onTimeUpdate={updateProgress}
            onLoadedMetadata={handleLoadedMetadata}
            onDurationChange={updateProgress}
          />
        )}
        <button className="player-main" onClick={() => onExpandedChange(!expanded)} aria-label={expanded ? t('collapsePlayer') : t('expandPlayer')}>
          <Artwork item={item} size="mini" />
          <span className="player-copy">
            <strong>{item.title}</strong>
            <span>{item.subtitle}</span>
          </span>
        </button>
        <button className="icon-button" onClick={() => movePlayer(-1)} disabled={!canGoPrevious} aria-label={t('previousTrack')}>
          <SkipBack size={18} />
        </button>
        <button className="icon-button" onClick={() => setPlayer((current) => ({ ...current, playing: !current.playing }))} aria-label={playPauseLabel}>
          {player.playing ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <button className="icon-button" onClick={() => movePlayer(1)} disabled={!canGoNext} aria-label={t('nextTrack')}>
          <SkipForward size={18} />
        </button>
        <button className="icon-button" onClick={() => navigate('detail', item.id)} aria-label={t('openDetail')}>
          <ChevronRight size={18} />
        </button>
        {!expanded && <PlayerProgress currentTime={progress.currentTime} duration={progress.duration} onSeek={seekPreview} t={t} />}
      </div>
      {expanded && (
        <section className={`player-expanded ${showVideoPreview ? 'is-video' : ''}`} role="dialog" aria-label={t('nowPlaying')}>
          <div className="player-expanded-head">
            <span>{t('nowPlaying')}</span>
            <button className="icon-button" onClick={() => onExpandedChange(false)} aria-label={t('collapsePlayer')}>
              <X size={18} />
            </button>
          </div>
          {showVideoPreview ? (
            <div className="player-video-wrap">
              <video
                ref={attachMedia}
                className="player-video"
                src={item.previewUrl}
                poster={item.artworkUrl}
                playsInline
                onEnded={() => movePlayer(1)}
                onTimeUpdate={updateProgress}
                onLoadedMetadata={handleLoadedMetadata}
                onDurationChange={updateProgress}
              />
              <button className="icon-button player-video-expand" onClick={() => onOpenVideo(item, progress.currentTime)} aria-label={t('enlargePreview')}>
                <Maximize2 size={17} />
              </button>
            </div>
          ) : (
            <Artwork item={item} size="playerLarge" />
          )}
          <div className="player-expanded-copy">
            <h2>{item.title}</h2>
            <p>{item.subtitle || item.primaryGenreName || item.kind}</p>
          </div>
          <PlayerProgress currentTime={progress.currentTime} duration={progress.duration} onSeek={seekPreview} t={t} />
          <div className="player-expanded-controls">
            <button className="icon-button" onClick={() => movePlayer(-1)} disabled={!canGoPrevious} aria-label={t('previousTrack')}>
              <SkipBack size={20} />
            </button>
            <button className="icon-button player-large-toggle" onClick={() => setPlayer((current) => ({ ...current, playing: !current.playing }))} aria-label={playPauseLabel}>
              {player.playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <button className="icon-button" onClick={() => movePlayer(1)} disabled={!canGoNext} aria-label={t('nextTrack')}>
              <SkipForward size={20} />
            </button>
          </div>
          <div className="action-row">
            <button className="icon-text-button" onClick={() => navigate('detail', item.id)}><ChevronRight size={17} /><span>{t('openDetail')}</span></button>
            {item.officialUrl && <a className="icon-text-button" href={item.officialUrl} target="_blank" rel="noreferrer"><ExternalLink size={17} /><span>{t('openApple')}</span></a>}
          </div>
          {player.queue.length > 1 && (
            <div className="player-queue">
              <button className="queue-toggle" onClick={() => setQueueOpen((current) => !current)}>
                <span>{t('queue')}</span>
                <span>{player.index + 1} / {player.queue.length}</span>
              </button>
              {queueOpen && (
                <div className="queue-list">
                  {player.queue.map((queueItem, index) => (
                    <button key={`${queueItem.id}-${index}`} className={`queue-item ${index === player.index ? 'is-active' : ''}`} onClick={() => selectQueueItem(index)}>
                      <Artwork item={queueItem} size="mini" />
                      <span className="queue-copy">
                        <strong>{queueItem.title}</strong>
                        <small>{queueItem.subtitle || queueItem.primaryGenreName || queueItem.kind}</small>
                      </span>
                      {index === player.index && <Play size={15} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </>
  );
}

function PlayerProgress({ currentTime, duration, onSeek, t }: { currentTime: number; duration: number; onSeek: (event: React.ChangeEvent<HTMLInputElement>) => void; t: ReturnType<typeof createTranslator> }) {
  const safeDuration = duration > 0 ? duration : 0;
  const safeCurrentTime = safeDuration > 0 ? Math.min(currentTime, safeDuration) : 0;
  return (
    <div className="player-progress">
      <span>{formatPlaybackTime(safeCurrentTime)}</span>
      <input
        type="range"
        min="0"
        max={safeDuration || 1}
        step="0.1"
        value={safeCurrentTime}
        disabled={!safeDuration}
        onChange={onSeek}
        aria-label={t('progress')}
      />
      <span>{formatPlaybackTime(safeDuration)}</span>
    </div>
  );
}

function Artwork({ item, size = 'normal' }: { item: Pick<SearchItem, 'title' | 'artworkUrl'>; size?: 'mini' | 'small' | 'normal' | 'large' | 'playerLarge' }) {
  return (
    <span className={`artwork artwork-${size}`}>
      {item.artworkUrl ? <img src={item.artworkUrl} alt="" loading="lazy" /> : <BookOpen size={size === 'large' || size === 'playerLarge' ? 54 : 24} />}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Segmented<T extends string>({ value, options, onChange, compact, wrap }: { value: T; options: Array<{ value: T; label: string; icon?: typeof Search }>; onChange: (value: T) => void; compact?: boolean; wrap?: boolean }) {
  return (
    <div className={`segmented ${compact ? 'is-compact' : ''} ${wrap ? 'is-wrapped' : ''}`}>
      {options.map((option) => {
        const Icon = option.icon;
        return (
          <button key={option.value} type="button" className={value === option.value ? 'is-active' : ''} onClick={() => onChange(option.value)} title={option.label}>
            {Icon && <Icon size={15} />}
            {!compact && <span>{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

function SectionHeader({ title, meta, action }: { title: string; meta?: string; action?: React.ReactNode }) {
  return (
    <div className="section-header">
      <div>
        <h2>{title}</h2>
        {meta && <p>{meta}</p>}
      </div>
      {action}
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: typeof Search; text: string }) {
  return (
    <div className="empty-state">
      <Icon size={32} />
      <span>{text}</span>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="error-state">
      <Info size={20} />
      <span>{message}</span>
    </div>
  );
}

function InfoGrid({ rows }: { rows: Array<{ label: string; value?: string | number | null }> }) {
  const visibleRows = rows.filter((row) => row.value !== undefined && row.value !== null && row.value !== '');
  if (visibleRows.length === 0) return <p className="muted-text">-</p>;
  return (
    <dl className="info-grid">
      {visibleRows.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ArtistInfoBlock({ info, t }: { info: ArtistInfo; t: ReturnType<typeof createTranslator> }) {
  return (
    <div className="artist-info-block">
      {info.wikipedia?.imageUrl && <img src={info.wikipedia.imageUrl} alt="" loading="lazy" />}
      <div>
        {info.wikipedia?.summary && <p>{info.wikipedia.summary}</p>}
        <InfoGrid rows={[
          { label: t('wikipedia'), value: info.wikipedia?.title },
          { label: t('musicBrainz'), value: info.musicBrainz?.disambiguation || info.musicBrainz?.type },
          { label: t('country'), value: info.musicBrainz?.country },
          { label: t('activeYears'), value: info.musicBrainz?.lifeSpan }
        ]} />
        {info.musicBrainz?.tags.length ? (
          <div className="tag-row">
            {info.musicBrainz.tags.map((tag) => <span key={tag}>{tag}</span>)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ArtworkLightbox({ lightbox, onClose, t }: { lightbox: { url: string; title: string }; onClose: () => void; t: ReturnType<typeof createTranslator> }) {
  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={lightbox.title} onClick={onClose}>
      <button className="lightbox-close" onClick={onClose} aria-label={t('close')}><X size={20} /></button>
      <img src={lightbox.url} alt={lightbox.title} onClick={(event) => event.stopPropagation()} />
    </div>
  );
}

function VideoLightbox({ video, onClose, t }: { video: { url: string; title: string; poster?: string; startAt: number }; onClose: () => void; t: ReturnType<typeof createTranslator> }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const syncStartTime = () => {
    const player = videoRef.current;
    if (!player || video.startAt <= 0 || !Number.isFinite(player.duration)) return;
    player.currentTime = Math.min(video.startAt, Math.max(0, player.duration - 0.5));
  };

  return (
    <div className="video-lightbox" role="dialog" aria-modal="true" aria-label={video.title} onClick={onClose}>
      <button className="lightbox-close" onClick={onClose} aria-label={t('close')}><X size={20} /></button>
      <video
        ref={videoRef}
        src={video.url}
        poster={video.poster}
        controls
        autoPlay
        playsInline
        onLoadedMetadata={syncStartTime}
        onClick={(event) => event.stopPropagation()}
      />
    </div>
  );
}

function mediaInfoRows(item: SearchItem, t: ReturnType<typeof createTranslator>) {
  const raw = item.raw;
  return [
    { label: t('collection'), value: item.collectionName },
    { label: t('genre'), value: item.primaryGenreName },
    { label: t('duration'), value: formatMillis(raw.trackTimeMillis) },
    { label: t('trackNumber'), value: formatPair(raw.trackNumber, raw.trackCount) },
    { label: t('discNumber'), value: formatPair(raw.discNumber, raw.discCount) },
    { label: t('country'), value: item.country },
    { label: t('identifiers'), value: [`track ${raw.trackId ?? ''}`, `collection ${raw.collectionId ?? ''}`, `artist ${raw.artistId ?? ''}`].filter((part) => !part.endsWith(' ')).join(' / ') }
  ];
}

function countryLabel(code: string, locale: Locale) {
  return `${countryFlag(code)} ${countryName(code, locale)}`.trim();
}

function rssPresetIcon(id: string) {
  if (id.includes('songs')) return Music2;
  if (id.includes('albums')) return Disc3;
  if (id.includes('apps')) return Smartphone;
  if (id.includes('podcasts')) return Podcast;
  if (id.includes('audio')) return Headphones;
  if (id.includes('books')) return BookOpen;
  return Compass;
}

function previewActionLabel(item: SearchItem, t: ReturnType<typeof createTranslator>) {
  return isVideoItem(item) ? t('watchPreview') : t('playPreview');
}

function pauseActionLabel(item: SearchItem, t: ReturnType<typeof createTranslator>) {
  return isVideoItem(item) ? t('pauseWatchPreview') : t('pausePreview');
}

function hasUsefulPreviewAction(item: SearchItem, allowLookupPreview = false) {
  if (!item.previewUrl && !allowLookupPreview) return false;
  return isMusicPreviewItem(item) || isVideoItem(item);
}

function isMusicPreviewItem(item: SearchItem) {
  const facts = [
    item.kind,
    item.wrapperType,
    asRawString(item.raw.collectionType)
  ].filter(Boolean).join(' ').toLowerCase();
  if (/\b(song|songs|album|albums|music-video|music video)\b/.test(facts)) return true;
  return facts.includes('collection') && Boolean(item.officialUrl?.includes('/album/'));
}

function isVideoItem(item: SearchItem) {
  const haystack = [
    item.kind,
    item.wrapperType,
    asRawString(item.raw.trackType),
    asRawString(item.raw.collectionType)
  ].filter(Boolean).join(' ').toLowerCase();
  return /\b(movie|video|tv|film)\b/.test(haystack) || haystack.includes('feature-movie') || haystack.includes('shortfilm');
}

function asRawString(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function safeMediaTime(value: number) {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function formatPair(value: unknown, total: unknown) {
  if (typeof value !== 'number') return undefined;
  return typeof total === 'number' ? `${value} / ${total}` : value;
}

function formatMillis(value: unknown) {
  if (typeof value !== 'number') return undefined;
  const totalSeconds = Math.round(value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function formatPlaybackTime(value: number) {
  const totalSeconds = Math.floor(safeMediaTime(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function ShimmerGrid() {
  return (
    <div className="result-grid">
      {Array.from({ length: 8 }).map((_, index) => (
        <div className="media-card shimmer-card" key={index}>
          <div className="shimmer-art" />
          <div className="shimmer-line wide" />
          <div className="shimmer-line" />
        </div>
      ))}
    </div>
  );
}

function ShimmerRows() {
  return (
    <div className="compact-list">
      {Array.from({ length: 6 }).map((_, index) => <div className="shimmer-row" key={index} />)}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="page-stack">
      <div className="detail-hero">
        <div className="shimmer-art large" />
        <div className="detail-copy">
          <div className="shimmer-line wide" />
          <div className="shimmer-line" />
          <div className="shimmer-line short" />
        </div>
      </div>
    </div>
  );
}

function readRoute(): Route {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const [viewRaw, idRaw] = raw.split('/');
  const view = VALID_VIEWS.has(viewRaw as View) ? viewRaw as View : 'search';
  return { view, id: idRaw ? decodeURIComponent(idRaw) : undefined };
}

function relatedLookupFor(item: SearchItem): Record<string, string | number> | null {
  const collectionId = item.raw.collectionId;
  const artistId = item.raw.artistId;
  if (typeof collectionId === 'number' && item.wrapperType === 'track') return { id: collectionId, entity: 'song' };
  if (typeof artistId === 'number' && (item.kind?.includes('song') || item.wrapperType === 'artist')) return { id: artistId, entity: 'album' };
  return null;
}

function artworkDownloadUrl(item: SearchItem) {
  const params = new URLSearchParams({
    url: item.artworkUrl ?? '',
    name: `${item.title || 'artwork'}.jpg`
  });
  return `/api/artwork?${params.toString()}`;
}

type CommonProps = {
  t: ReturnType<typeof createTranslator>;
  locale: Locale;
  state: PersistedState;
  patchState: (patch: Partial<PersistedState>) => void;
  navigate: (view: View, id?: string) => void;
  toggleFavorite: (item: SearchItem) => void;
  isFavorite: (item: SearchItem) => boolean;
  playItem: (item: SearchItem, queue?: SearchItem[]) => void;
  playQueue: (queue: SearchItem[], index?: number) => void;
  movePlayer: (direction: -1 | 1) => void;
  openDetail: (item: SearchItem) => void;
  openArtwork: (url: string, title: string) => void;
  player: PlayerState;
};
